import { ref, computed, Ref } from 'vue';
import { CouponPromoTypeEnum } from '~mall/constants/apiConstants';
import type { SkuDetailData } from '~mall/model/store/modules/ticket';
import store from '~mall/model/store';

/**
 * 奖励金勾选逻辑 composable
 *
 * 封装详情页/浮层中奖励金勾选的通用逻辑：
 * - 判断奖励金在 promotionInfos 还是 optionalPromotions 中
 * - 控制勾选项的展示/隐藏
 * - 计算抵扣金额
 * - 管理勾选状态并同步到 reward store
 *
 * @param skuData - 商品详情数据的 ref
 */
export function useRewardCheckbox(skuData: Ref<SkuDetailData | null>) {
  /** 奖励金勾选状态 */
  const rewardChecked = ref(false);

  /** 奖励金是否在 promotionInfos 中（默认勾选） */
  const isRewardInPromo = computed(() => !!skuData.value?.promotionInfos?.some(
    (p) => String(p.couponPromoType) === CouponPromoTypeEnum.REWARD_POINTS,
  ));

  /** 奖励金是否在 optionalPromotions 中（默认未勾选） */
  const isRewardOptional = computed(() => !!skuData.value?.optionalPromotions?.some(
    (p) => String(p.couponPromoType) === CouponPromoTypeEnum.REWARD_POINTS,
  ));

  /** 是否展示奖励金勾选项 */
  const showRewardCheckbox = computed(() => {
    if (!skuData.value) return false;
    return isRewardInPromo.value || isRewardOptional.value;
  });

  /** 奖励金抵扣金额 */
  const rewardAmount = computed(() => {
    const data = skuData.value;
    if (!data) return 0;
    const allPromos = [
      ...(data.promotionInfos || []),
      ...(data.optionalPromotions || []),
    ];
    const rewardItem = allPromos.find(
      (p) => String(p.couponPromoType) === CouponPromoTypeEnum.REWARD_POINTS,
    );
    return rewardItem ? (Number(rewardItem.worth) || 0) : 0;
  });

  /** 获取奖励金默认勾选状态：在 promotionInfos 中 → 勾选；在 optionalPromotions 中 → 不勾选 */
  const getDefaultRewardChecked = (): boolean => {
    const data = skuData.value;
    if (!data) return false;
    return !!data.promotionInfos?.some(
      (p) => String(p.couponPromoType) === CouponPromoTypeEnum.REWARD_POINTS,
    );
  };

  /** 奖励金勾选状态变化处理 */
  const handleRewardChange = (val: boolean) => {
    rewardChecked.value = val;
    store.commit('reward/setRewardChecked', val);
  };

  /** 初始化奖励金勾选默认状态（在 skuData 加载完成后调用） */
  const initRewardChecked = () => {
    const defaultChecked = getDefaultRewardChecked();
    rewardChecked.value = defaultChecked;
    store.commit('reward/setRewardChecked', defaultChecked);
    store.commit('reward/setRewardCheckboxVisible', showRewardCheckbox.value);
  };

  /**
   * 构建下单用的 promotionInfos（根据奖励金勾选状态合并/移除奖励金项）
   * - 用户取消勾选奖励金：从 promotionInfos 中移除奖励金项
   * - 用户勾选奖励金且奖励金在 optionalPromotions 中：合并到 promotionInfos
   * @param data 当前商品数据（需包含 promotionInfos 和 optionalPromotions）
   */
  const getMergedPromotionInfos = (data: Pick<SkuDetailData, 'promotionInfos' | 'optionalPromotions'> | null) => {
    if (!data) return [];
    let baseInfos = [...(data.promotionInfos || [])];

    if (isRewardInPromo.value && !rewardChecked.value) {
      // 用户取消勾选，移除奖励金项
      baseInfos = baseInfos.filter(
        (p) => String(p.couponPromoType) !== CouponPromoTypeEnum.REWARD_POINTS,
      );
    } else if (isRewardOptional.value && rewardChecked.value) {
      // 用户勾选，合并 optionalPromotions 中的奖励金项
      const rewardItems = (data.optionalPromotions || []).filter(
        (p) => String(p.couponPromoType) === CouponPromoTypeEnum.REWARD_POINTS,
      );
      baseInfos = [...baseInfos, ...rewardItems];
    }

    return baseInfos;
  };

  return {
    rewardChecked,
    isRewardInPromo,
    isRewardOptional,
    showRewardCheckbox,
    rewardAmount,
    getDefaultRewardChecked,
    handleRewardChange,
    initRewardChecked,
    getMergedPromotionInfos,
  };
}
